import type { IStandardResponse } from '@/entities/common/types/api';
import type {
  IAuthLoginResponse,
  IAuthConfirmResponse,
  IPatientProfileResponse,
  IUpdatePatientProfileRequest,
  IUpdatePatientProfileResponse,
  IEMAIRedirectResponse,
} from '@/entities/common/types/auth';

import type { HttpClient } from '../http';

/**
 * Репозиторий для работы с аутентификацией пользователей
 */
class AuthRepository {
  private http: HttpClient;

  constructor(http: HttpClient) {
    this.http = http;
  }

  /**
   * Отправляет OTP код на указанный номер телефона
   * @param phoneNumber - Номер телефона для отправки OTP
   * @returns Ответ от сервера с информацией об отправке OTP
   */
  public async login(phoneNumber: string): Promise<IAuthLoginResponse> {
    try {
      const data = await this.http.post<IAuthLoginResponse>(`/patient/auth/login`, { phoneNumber });

      return data;
    } catch (error: unknown) {
      console.error('Auth login error:', error);

      return {
        code: 0,
        message: error instanceof Error ? error.message : 'Ошибка отправки OTP',
      };
    }
  }

  /**
   * Подтверждает OTP код и получает токен доступа
   * @param phoneNumber - Номер телефона
   * @param code - OTP код
   * @returns Ответ от сервера с токеном доступа
   */
  public async confirm(phoneNumber: string, code: string): Promise<IAuthConfirmResponse> {
    try {
      const data = await this.http.post<IAuthConfirmResponse>(`/patient/auth/confirm`, { phoneNumber, code });

      return data;
    } catch (error: unknown) {
      console.error('Auth confirm error:', error);

      return {
        code: 0,
        message: error instanceof Error ? error.message : 'Ошибка подтверждения OTP',
        data: {
          accessToken: '',
          tokenType: '',
          expiresIn: 0,
        },
      };
    }
  }

  /**
   * Получает профиль пациента
   * @param accessToken - Токен доступа
   * @returns Профиль пациента
   */
  public async getPatientProfile(accessToken: string): Promise<IPatientProfileResponse> {
    try {
      const data = await this.http.get<IPatientProfileResponse>(`/patient/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return data;
    } catch (error: unknown) {
      console.error('Get patient profile error:', error);

      return {
        code: 0,
        message: error instanceof Error ? error.message : 'Ошибка получения профиля',
        data: {
          id: 0,
          firstName: null,
          lastName: null,
          patronymic: null,
          iin: null,
        },
      };
    }
  }

  /**
   * Обновляет профиль пациента
   * @param accessToken - Токен доступа
   * @param profileData - Данные для обновления профиля
   * @returns Обновленный профиль пациента
   */
  public async updatePatientProfile(
    accessToken: string,
    profileData: IUpdatePatientProfileRequest,
  ): Promise<IUpdatePatientProfileResponse | string> {
    try {
      const data = await this.http.patch<IUpdatePatientProfileResponse>(`/patient/me`, profileData, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      return data;
    } catch (error: unknown) {
      console.error('Update patient profile error:', error);

      return error instanceof Error ? error.message : 'Ошибка обновления профиля';
      // return {
      //   code: 0,
      //   message: error instanceof Error ? error.message : 'Ошибка обновления профиля',
      //   data: {
      //     id: 0,
      //     firstName: null,
      //     lastName: null,
      //     patronymic: null,
      //     iin: null,
      //   },
      // };
    }
  }

  public async EMAIRedirect(): Promise<IEMAIRedirectResponse | null> {
    try {
      const response = await this.http.get<IStandardResponse<IEMAIRedirectResponse>>(`/duty-doctor/appointment`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
        },
      });

      return response.data;
    } catch (error: unknown) {
      console.error('EMAIRedirect error:', error);

      return null;
    }
  }
}

export { AuthRepository };
